//-tech-debt-react-query;
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { User } from "../types/user";
import { fetchItems, postItem } from "../support/queries";
import { CacheKey } from "../types/cache-keys";
import { DEFAULT_QUERY_OPTIONS } from "../support/constants";
import { useSelector } from "react-redux";
import { RootState } from "../support/store";


const useFetchUser = (
  params?: { email?: string; detailed?: boolean },
  options?
): UseQueryResult<User, unknown> => {
  const { email } = useSelector((state: RootState) => state.user.userData);
  const requestParams = {
    ...params,
    detailed: params?.detailed ?? true,
    email: params?.email || email
  };

  return useQuery({
    queryKey: [CacheKey.User],
    queryFn: () => fetchItems(`db/user`, requestParams, { credentials: 'same-origin' }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        staleTime: Infinity,
        gcTime: Infinity,
        select: (data: User[] = []) => {
          return Array.isArray(data) && data.length > 0 ? data[0] : null
        },
        enabled: !!requestParams.email,
        ...options,
      },
  });
}

const useCreateUserMutation = (userData: { email: string, username: string }) => {
  const queryClient = useQueryClient();

  return useMutation<User, Error, { email: string; username: string }>({
    mutationFn: (userData) => {
      const newUser = {
        username: userData.username,
        email: userData.email,
        role: 'user',
        registry: { registryValue: {} },
        created: new Date().toISOString().split('T')[0],
        updated: new Date().toISOString().split('T')[0],
      };
      return postItem('user', newUser);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [CacheKey.User] });
    },
    onSuccess: (response, _variables) => {
      queryClient.setQueryData<User[]>([CacheKey.User], () => [response]);
    },
    onError: (_error, _variables) => {
      console.error('Error creating user:', _error);
      queryClient.setQueryData([CacheKey.User], []);
    },
    onSettled: (_response, _error, _variables) => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.User] });
    },
  })
};


export {
  useFetchUser,
  useCreateUserMutation,
}